/**
 * Immo 360 / JLR property-data parsers and normalizers.
 * Shared by sync/async import and admin edit callables.
 * Pure helpers only — no Firestore I/O.
 */

import { normalizeHeader } from "./prospectImportMapping.js";

export const ADMIN_PROSPECT_IMPORT_SCHEMA_VERSION = 2;
export const ADMIN_PROSPECT_IMPORT_SCHEMA_VERSION_LEGACY = 1;

export const IMPORT_PROVENANCE_SOURCE_TYPES = Object.freeze(["jlr_csv", "generic_csv", "admin_edit"]);

export const LAND_AREA_UNITS = Object.freeze(["m2", "ft2"]);
export const LAND_AREA_MAX_M2 = 10_000_000;
export const LAND_AREA_MAX_FT2 = 107_639_104; // ~10_000_000 m²

/** Whole-dollar municipal assessments / consideration — max CAD $500M → cents. */
export const JLR_MONEY_MAX_CENTS = 50_000_000_000;

export const CONSTRUCTION_YEAR_MIN = 1800;
export const NUMBER_OF_FLOORS_MAX = 200;
export const LOT_NUMBER_MAX_LEN = 64;
export const LOT_NUMBERS_MAX = 32;
export const CADASTRAL_DIVISION_MAX_LEN = 120;
export const MUNICIPAL_ROLL_MAX_LEN = 64;
export const BUILDING_LABEL_MAX_LEN = 120;
export const REGISTRY_LABEL_MAX_LEN = 120;
export const REGISTRATION_NUMBER_MAX_LEN = 80;

export const REGISTRY_ACTIVITY_TYPES = Object.freeze([
  "sale",
  "mortgage",
  "transmission",
  "assignment_or_donation",
  "undivided_sale",
  "sixty_day_notice",
  "correction_assignment",
  "other",
]);

const REGISTRY_LABEL_TO_TYPE = Object.freeze({
  vente: "sale",
  hypotheque: "mortgage",
  transmission: "transmission",
  cession: "assignment_or_donation",
  donation: "assignment_or_donation",
  "cession donation": "assignment_or_donation",
  "vente indivise": "undivided_sale",
  "avis de 60 jours": "sixty_day_notice",
  "avis de 60 jour": "sixty_day_notice",
  "correction cession": "correction_assignment",
  "correction - cession": "correction_assignment",
});

const SQFT_TO_SQM_NUM = 9290304n;
const SQFT_TO_SQM_DEN = 100_000_000n;

function boundedTrim(value, max) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;
  if (text.length > max) return { error: "too_long" };
  return text;
}

/**
 * Parse whole-dollar JLR money into integer CAD cents (no float multiply).
 * Blank / zero → null (unavailable) unless allowZero is true.
 * Fail-closed on ambiguous separators or nonzero fractions (never inflate).
 */
export function parseJlrWholeDollarToCents(raw, { allowZero = false } = {}) {
  if (raw == null) return { ok: true, cents: null };
  let text = String(raw).trim();
  if (!text) return { ok: true, cents: null };
  text = text.replace(/CAD/gi, "").replace(/\$/g, "").trim();
  if (!text) return { ok: true, cents: null };
  if (/[eE]/.test(text) || /[-−]/.test(text)) return { ok: false, reason: "invalid" };

  // Keep only digits and grouping/decimal separators after removing spaces.
  const compact = text.replace(/[\s\u00a0\u202f]/g, "");
  if (!compact) return { ok: true, cents: null };
  if (!/^[0-9.,]+$/.test(compact)) return { ok: false, reason: "invalid" };

  const commaCount = (compact.match(/,/g) || []).length;
  const periodCount = (compact.match(/\./g) || []).length;
  let wholeDigits = null;

  if (commaCount === 0 && periodCount === 0) {
    wholeDigits = compact;
  } else if (periodCount === 0 && commaCount >= 1) {
    // EN thousands (one or more groups) or unambiguous terminal ,00
    if (commaCount >= 1 && /,\d{2}$/.test(compact) && !/\.\d+$/.test(compact)) {
      const [left, right = ""] = compact.split(/,(?=[^,]*$)/);
      if (right === "00") {
        const leftCompact = left;
        if (!leftCompact || !/^\d+$/.test(leftCompact.replace(/,/g, ""))) {
          return { ok: false, reason: "invalid" };
        }
        if (leftCompact.includes(",")) {
          if (!/^\d{1,3}(,\d{3})+$/.test(leftCompact)) return { ok: false, reason: "invalid" };
          wholeDigits = leftCompact.replace(/,/g, "");
        } else {
          wholeDigits = leftCompact;
        }
      } else {
        return { ok: false, reason: "invalid" };
      }
    } else if (/^\d{1,3}(,\d{3})+$/.test(compact)) {
      wholeDigits = compact.replace(/,/g, "");
    } else {
      return { ok: false, reason: "invalid" };
    }
  } else if (commaCount === 0 && periodCount >= 1) {
    // EU thousands (one or more groups) or unambiguous terminal .00
    if (periodCount >= 1 && /\.\d{2}$/.test(compact) && !/,\d+$/.test(compact)) {
      const [left, right = ""] = compact.split(/\.(?=[^.]*$)/);
      if (right === "00") {
        if (!left || !/^\d+$/.test(left.replace(/\./g, ""))) return { ok: false, reason: "invalid" };
        if (left.includes(".")) {
          if (!/^\d{1,3}(\.\d{3})+$/.test(left)) return { ok: false, reason: "invalid" };
          wholeDigits = left.replace(/\./g, "");
        } else {
          wholeDigits = left;
        }
      } else {
        // Nonzero/ambiguous decimal fraction — fail closed (never inflate).
        return { ok: false, reason: "invalid" };
      }
    } else if (/^\d{1,3}(\.\d{3})+$/.test(compact)) {
      wholeDigits = compact.replace(/\./g, "");
    } else {
      return { ok: false, reason: "invalid" };
    }
  } else {
    // Unambiguous thousands of one separator + terminal zero fraction of the other:
    // 1,234,567.00 or 1.234.567,00 — still reject nonzero fractions like 1,234.56.
    if (/^\d{1,3}(,\d{3})+\.00$/.test(compact)) {
      wholeDigits = compact.replace(/\.00$/, "").replace(/,/g, "");
    } else if (/^\d{1,3}(\.\d{3})+,00$/.test(compact)) {
      wholeDigits = compact.replace(/,00$/, "").replace(/\./g, "");
    } else {
      // Mixed or ambiguous separators (e.g. 1.234,56 or 1,234.56) — fail closed.
      return { ok: false, reason: "invalid" };
    }
  }

  if (!wholeDigits || !/^\d+$/.test(wholeDigits)) return { ok: false, reason: "invalid" };
  try {
    const dollars = BigInt(wholeDigits);
    if (dollars === 0n) {
      return allowZero ? { ok: true, cents: 0 } : { ok: true, cents: null };
    }
    const cents = dollars * 100n;
    if (cents > BigInt(JLR_MONEY_MAX_CENTS)) return { ok: false, reason: "upperBound" };
    return { ok: true, cents: Number(cents) };
  } catch {
    return { ok: false, reason: "invalid" };
  }
}

/**
 * Strict JLR date: YYYY-MM-DD or YYYY/MM/DD or DD/MM/YYYY (Quebec export).
 * Never returns epoch for blank/malformed.
 */
export function parseJlrActivityDate(raw) {
  if (raw == null) return { ok: true, isoDate: null };
  const text = String(raw).trim();
  if (!text) return { ok: true, isoDate: null };

  let year;
  let month;
  let day;
  let m = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) {
    year = Number(m[1]);
    month = Number(m[2]);
    day = Number(m[3]);
  } else {
    m = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (!m) return { ok: false, reason: "invalid" };
    day = Number(m[1]);
    month = Number(m[2]);
    year = Number(m[3]);
  }
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return { ok: false, reason: "invalid" };
  }
  if (year < 1800 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
    return { ok: false, reason: "invalid" };
  }
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() !== month - 1 ||
    dt.getUTCDate() !== day
  ) {
    return { ok: false, reason: "invalid" };
  }
  const isoDate = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return { ok: true, isoDate };
}

export function normalizeRegistryActivityType(raw) {
  if (raw == null) return { ok: true, activityType: null, originalLabel: null };
  const original = String(raw).trim();
  if (!original) return { ok: true, activityType: null, originalLabel: null };
  if (original.length > REGISTRY_LABEL_MAX_LEN) {
    return { ok: false, reason: "too_long" };
  }
  const key = normalizeHeader(original);
  const mapped = REGISTRY_LABEL_TO_TYPE[key];
  if (mapped) return { ok: true, activityType: mapped, originalLabel: original.slice(0, REGISTRY_LABEL_MAX_LEN) };
  return {
    ok: true,
    activityType: "other",
    originalLabel: original.slice(0, REGISTRY_LABEL_MAX_LEN),
  };
}

export function parseConstructionYear(raw, { nowYear = new Date().getFullYear() } = {}) {
  if (raw == null || raw === "") return { ok: true, year: null };
  const text = String(raw).trim();
  if (!text) return { ok: true, year: null };
  if (!/^-?\d+$/.test(text)) return { ok: false, reason: "invalid" };
  const year = Number(text);
  if (year === 0) return { ok: true, year: null };
  if (!Number.isInteger(year) || year < CONSTRUCTION_YEAR_MIN || year > nowYear) {
    return { ok: false, reason: "invalid" };
  }
  return { ok: true, year };
}

export function parseNumberOfFloors(raw) {
  if (raw == null || raw === "") return { ok: true, floors: null };
  const text = String(raw).trim();
  if (!text) return { ok: true, floors: null };
  if (!/^\d+$/.test(text)) return { ok: false, reason: "invalid" };
  const floors = Number(text);
  if (floors === 0) return { ok: true, floors: null };
  if (!Number.isInteger(floors) || floors < 1 || floors > NUMBER_OF_FLOORS_MAX) {
    return { ok: false, reason: "invalid" };
  }
  return { ok: true, floors };
}

export function parseBoundedBuildingLabel(raw) {
  const result = boundedTrim(raw, BUILDING_LABEL_MAX_LEN);
  if (result && typeof result === "object" && result.error) return { ok: false, reason: result.error };
  return { ok: true, value: result };
}

/**
 * Normalize / dedupe lot numbers. Client never supplies match keys.
 */
export function normalizeLotNumbers(raw) {
  const list = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(/[;,|/]/)
      : raw == null || raw === ""
        ? []
        : [raw];
  const seen = new Set();
  const display = [];
  for (const item of list) {
    if (item == null) continue;
    const text = String(item).trim().replace(/\s+/g, " ");
    if (!text) continue;
    if (text.length > LOT_NUMBER_MAX_LEN) return { ok: false, reason: "too_long" };
    const key = normalizeCadastralLotKey(text);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    display.push(text);
    if (display.length > LOT_NUMBERS_MAX) return { ok: false, reason: "too_many" };
  }
  return { ok: true, lotNumbers: display };
}

export function parseCadastralDivision(raw) {
  const result = boundedTrim(raw, CADASTRAL_DIVISION_MAX_LEN);
  if (result && typeof result === "object" && result.error) return { ok: false, reason: result.error };
  return { ok: true, value: result };
}

export function parseMunicipalRollNumber(raw) {
  const result = boundedTrim(raw, MUNICIPAL_ROLL_MAX_LEN);
  if (result && typeof result === "object" && result.error) return { ok: false, reason: result.error };
  return { ok: true, value: result };
}

/**
 * Dedicated cadastral-lot canonicalizer (not the generic address normalizer).
 * Equivalent presentation (spaces / hyphens) collapses consistently.
 */
export function normalizeCadastralLotKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "")
    .trim();
}

export function normalizeCadastralDivisionKey(value) {
  return normalizeCadastralLotKey(value);
}

/**
 * Server-derived lot match keys. Never accept from client.
 * Format: `{divisionKey}|{lotKey}`.
 * Automatic keys are generated ONLY when cadastral division context is present
 * and valid — a bare lot number must not authorize automatic matching.
 */
export function buildLotMatchKeys(lotNumbers, cadastralDivision) {
  const lots = Array.isArray(lotNumbers) ? lotNumbers : [];
  const divisionKey = normalizeCadastralDivisionKey(cadastralDivision);
  if (!divisionKey) return [];
  const keys = [];
  const seen = new Set();
  for (const lot of lots) {
    const lotKey = normalizeCadastralLotKey(lot);
    if (!lotKey) continue;
    const key = `${divisionKey}|${lotKey}`;
    if (seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

function parseFiniteNumber(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return null;
    return raw;
  }
  const text = String(raw).trim().replace(/[\s\u00a0\u202f]/g, "").replace(",", ".");
  if (!text) return null;
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

export function normalizeLandAreaUnit(raw) {
  if (raw == null || raw === "") return null;
  const key = normalizeHeader(raw).replace(/\s+/g, "");
  if (key === "m2" || key === "m" || key === "metre" || key === "metres" || key === "sqm" || key === "metre2") {
    return "m2";
  }
  if (
    key === "ft2" ||
    key === "pi2" ||
    key === "sqft" ||
    key === "sqfeet" ||
    key === "pied" ||
    key === "pieds" ||
    key === "pied2" ||
    key === "piedscarres" ||
    key === "squarefeet" ||
    key === "squarefoot"
  ) {
    return "ft2";
  }
  return null;
}

/**
 * Deterministic ft² → m² using fixed-point BigInt (no float multiply of source).
 * Returns rounded square metres to 4 decimal places as a number, or null.
 */
export function squareFeetToSquareMetres(ft2) {
  if (ft2 == null || !Number.isFinite(ft2) || ft2 <= 0) return null;
  // Scale input to micro-units (6 decimals) then convert.
  const scaled = BigInt(Math.round(ft2 * 1_000_000));
  const m2Scaled = (scaled * SQFT_TO_SQM_NUM) / SQFT_TO_SQM_DEN;
  // m2Scaled is in micro-m²; round to 4 decimal places → divide by 100 then / 10000
  const rounded = Number(m2Scaled) / 1_000_000;
  if (!Number.isFinite(rounded) || rounded <= 0) return null;
  return Math.round(rounded * 10000) / 10000;
}

export function parseLandArea(valueRaw, unitRaw) {
  const unit = normalizeLandAreaUnit(unitRaw);
  const value = parseFiniteNumber(valueRaw);
  if (value == null && (unitRaw == null || String(unitRaw).trim() === "")) {
    return { ok: true, landArea: null };
  }
  if (value == null || value <= 0 || !unit) {
    if (value == null && !unit) return { ok: true, landArea: null };
    return { ok: false, reason: "invalid" };
  }
  if (unit === "m2" && value > LAND_AREA_MAX_M2) return { ok: false, reason: "upperBound" };
  if (unit === "ft2" && value > LAND_AREA_MAX_FT2) return { ok: false, reason: "upperBound" };
  const squareMetres = unit === "m2" ? Math.round(value * 10000) / 10000 : squareFeetToSquareMetres(value);
  if (squareMetres == null || squareMetres <= 0) return { ok: false, reason: "invalid" };
  return {
    ok: true,
    landArea: {
      value,
      unit,
      squareMetres,
    },
  };
}

export function buildImportProvenance({ sourceType, importId, importedAt }) {
  const type = IMPORT_PROVENANCE_SOURCE_TYPES.includes(sourceType) ? sourceType : "generic_csv";
  const id = typeof importId === "string" && importId.trim() ? importId.trim().slice(0, 128) : null;
  const at =
    typeof importedAt === "string" && importedAt
      ? importedAt
      : importedAt && typeof importedAt.toDate === "function"
        ? importedAt.toDate().toISOString()
        : null;
  return {
    sourceType: type,
    importId: id,
    importedAt: at,
  };
}

/**
 * Extract JLR property enrichment from a raw CSV row given profile field indexes.
 */
export function extractJlrPropertyEnrichment(rawRow, profile, { nowYear = new Date().getFullYear() } = {}) {
  const warnings = [];
  const fields = profile?.fields && typeof profile.fields === "object" ? profile.fields : {};
  const cell = (key) => {
    const field = fields[key];
    if (field?.kind !== "column" || !Number.isInteger(field.index)) return "";
    const raw = rawRow?.[field.index];
    return raw == null ? "" : String(raw).trim();
  };

  const evaluation = parseJlrWholeDollarToCents(cell("evaluation"));
  if (!evaluation.ok) warnings.push("evaluation_invalid");

  const consideration = parseJlrWholeDollarToCents(cell("contrepartie"));
  if (!consideration.ok) warnings.push("contrepartie_invalid");

  const activityDate = parseJlrActivityDate(cell("derniereTransaction"));
  if (!activityDate.ok) warnings.push("transaction_date_invalid");

  const activityType = normalizeRegistryActivityType(cell("nomTransaction"));
  if (!activityType.ok) warnings.push("transaction_type_invalid");

  const buildingCategory = parseBoundedBuildingLabel(cell("categorieBatiment"));
  if (!buildingCategory.ok) warnings.push("building_category_invalid");

  const constructionType = parseBoundedBuildingLabel(cell("typeConstruction"));
  if (!constructionType.ok) warnings.push("construction_type_invalid");

  const floors = parseNumberOfFloors(cell("nombreEtages"));
  if (!floors.ok) warnings.push("floors_invalid");

  const year = parseConstructionYear(cell("anneeConstruction"), { nowYear });
  if (!year.ok) warnings.push("construction_year_invalid");

  const lots = normalizeLotNumbers(cell("lotNumbers"));
  if (!lots.ok) warnings.push("lot_numbers_invalid");

  const division = parseCadastralDivision(cell("cadastralDivision"));
  if (!division.ok) warnings.push("cadastral_division_invalid");

  const roll = parseMunicipalRollNumber(cell("municipalRollNumber"));
  if (!roll.ok) warnings.push("municipal_roll_invalid");

  const landArea = parseLandArea(cell("landAreaValue"), cell("landAreaUnit") || "m2");
  if (!landArea.ok) warnings.push("land_area_invalid");

  const enrichment = {
    buildingCategory: buildingCategory.ok ? buildingCategory.value : null,
    constructionType: constructionType.ok ? constructionType.value : null,
    numberOfFloors: floors.ok ? floors.floors : null,
    constructionYear: year.ok ? year.year : null,
    lotNumbers: lots.ok ? lots.lotNumbers : [],
    cadastralDivision: division.ok ? division.value : null,
    municipalRollNumber: roll.ok ? roll.value : null,
    landArea: landArea.ok ? landArea.landArea : null,
    municipalAssessmentTotalCents: evaluation.ok ? evaluation.cents : null,
    registryActivity: null,
  };

  const hasActivity =
    (activityType.ok && activityType.activityType) ||
    (activityDate.ok && activityDate.isoDate) ||
    (consideration.ok && consideration.cents != null);
  if (hasActivity && activityDate.ok && activityType.ok) {
    enrichment.registryActivity = {
      activityType: activityType.activityType,
      originalLabel: activityType.originalLabel,
      activityDate: activityDate.isoDate,
      considerationCents: consideration.ok ? consideration.cents : null,
      registrationNumber: null,
      transferredPercentage: null,
    };
  } else if (!activityDate.ok && (activityType.activityType || consideration.cents != null)) {
    // Malformed optional activity → omit activity, keep warning.
  }

  return { enrichment, warnings };
}

/**
 * Build stored nested sections from validated enrichment + provenance.
 */
export function buildImportedPropertySections(enrichment, provenance) {
  const src = enrichment && typeof enrichment === "object" ? enrichment : {};
  const prov = provenance && typeof provenance === "object" ? provenance : null;
  const lotNumbers = Array.isArray(src.lotNumbers) ? src.lotNumbers : [];
  const profile = {
    buildingCategory: src.buildingCategory ?? null,
    constructionType: src.constructionType ?? null,
    numberOfUnits: src.numberOfUnits ?? null,
    numberOfFloors: src.numberOfFloors ?? null,
    constructionYear: src.constructionYear ?? null,
    landArea: src.landArea ?? null,
    lotNumbers,
    cadastralDivision: src.cadastralDivision ?? null,
    municipalRollNumber: src.municipalRollNumber ?? null,
  };
  if (prov) profile.provenance = prov;

  const assessment = {
    totalCents: src.municipalAssessmentTotalCents ?? null,
    landCents: src.municipalAssessmentLandCents ?? null,
    buildingCents: src.municipalAssessmentBuildingCents ?? null,
    referenceYear: src.assessmentReferenceYear ?? null,
    referenceDate: src.assessmentReferenceDate ?? null,
  };
  if (prov) assessment.provenance = prov;

  let activity = null;
  if (src.registryActivity && typeof src.registryActivity === "object") {
    activity = {
      activityType: src.registryActivity.activityType ?? null,
      originalLabel: src.registryActivity.originalLabel ?? null,
      activityDate: src.registryActivity.activityDate ?? null,
      considerationCents: src.registryActivity.considerationCents ?? null,
      registrationNumber: src.registryActivity.registrationNumber ?? null,
      transferredPercentage: src.registryActivity.transferredPercentage ?? null,
    };
    if (prov) activity.provenance = prov;
  }

  const hasProfile =
    profile.buildingCategory ||
    profile.constructionType ||
    profile.numberOfUnits != null ||
    profile.numberOfFloors != null ||
    profile.constructionYear != null ||
    profile.landArea ||
    lotNumbers.length ||
    profile.cadastralDivision ||
    profile.municipalRollNumber;
  const hasAssessment =
    assessment.totalCents != null ||
    assessment.landCents != null ||
    assessment.buildingCents != null ||
    assessment.referenceYear != null ||
    assessment.referenceDate;
  return {
    latestImportedPropertyProfile: hasProfile ? profile : null,
    latestMunicipalAssessment: hasAssessment ? assessment : null,
    latestRegistryActivity: activity,
    lotNumbers,
    cadastralDivision: profile.cadastralDivision,
    municipalRollNumber: profile.municipalRollNumber,
    lotMatchKeys: buildLotMatchKeys(lotNumbers, profile.cadastralDivision),
  };
}

/** Stable JSON compare for change detection (sorted keys). */
export function stableSerialize(value) {
  if (value == null) return "null";
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableSerialize(value[k])}`).join(",")}}`;
}

export function valuesEqual(a, b) {
  return stableSerialize(a) === stableSerialize(b);
}

/**
 * Overlay only validated nonblank incoming fields onto an existing nested section.
 * Blank / null / empty-array incoming values never erase existing data.
 */
export function mergeImportedProfileSection(existingProfile, incomingProfile, provenance) {
  const existing =
    existingProfile && typeof existingProfile === "object" && !Array.isArray(existingProfile)
      ? { ...existingProfile }
      : {};
  const incoming =
    incomingProfile && typeof incomingProfile === "object" && !Array.isArray(incomingProfile)
      ? incomingProfile
      : {};
  const merged = { ...existing };
  let changed = false;

  const applyScalar = (key) => {
    const value = incoming[key];
    if (value == null || value === "") return;
    if ((key === "constructionYear" || key === "numberOfFloors" || key === "numberOfUnits") && value === 0) {
      return;
    }
    if (Array.isArray(value) && !value.length) return;
    if (valuesEqual(existing[key], value)) return;
    merged[key] = value;
    changed = true;
  };

  applyScalar("buildingCategory");
  applyScalar("constructionType");
  applyScalar("numberOfUnits");
  applyScalar("numberOfFloors");
  applyScalar("constructionYear");
  applyScalar("landArea");
  applyScalar("cadastralDivision");
  applyScalar("municipalRollNumber");
  if (Array.isArray(incoming.lotNumbers) && incoming.lotNumbers.length) {
    if (!valuesEqual(existing.lotNumbers || [], incoming.lotNumbers)) {
      merged.lotNumbers = incoming.lotNumbers;
      changed = true;
    }
  }

  if (!changed) {
    const hasData =
      merged.buildingCategory ||
      merged.constructionType ||
      merged.numberOfUnits != null ||
      merged.numberOfFloors != null ||
      merged.constructionYear != null ||
      merged.landArea ||
      (Array.isArray(merged.lotNumbers) && merged.lotNumbers.length) ||
      merged.cadastralDivision ||
      merged.municipalRollNumber;
    return { section: hasData ? existingProfile || null : null, changed: false };
  }
  if (provenance) merged.provenance = provenance;
  return { section: merged, changed: true };
}

function assessmentHasMeaningfulData(assessment) {
  if (!assessment || typeof assessment !== "object") return false;
  return (
    assessment.totalCents != null ||
    assessment.landCents != null ||
    assessment.buildingCents != null ||
    assessment.referenceYear != null ||
    Boolean(assessment.referenceDate)
  );
}

function registryHasMeaningfulData(activity) {
  if (!activity || typeof activity !== "object") return false;
  return Boolean(
    activity.activityType ||
      activity.originalLabel ||
      activity.activityDate ||
      activity.considerationCents != null ||
      activity.registrationNumber ||
      activity.transferredPercentage != null,
  );
}

function buildAssessmentSnapshot(incoming, provenance) {
  const section = {
    totalCents: incoming.totalCents ?? null,
    landCents: incoming.landCents ?? null,
    buildingCents: incoming.buildingCents ?? null,
    referenceYear: incoming.referenceYear ?? null,
    referenceDate: incoming.referenceDate ?? null,
  };
  if (!assessmentHasMeaningfulData(section)) return null;
  if (provenance) section.provenance = provenance;
  return section;
}

function buildRegistrySnapshot(incoming, provenance) {
  const section = {
    activityType: incoming.activityType ?? null,
    originalLabel: incoming.originalLabel ?? null,
    activityDate: incoming.activityDate ?? null,
    considerationCents: incoming.considerationCents ?? null,
    registrationNumber: incoming.registrationNumber ?? null,
    transferredPercentage: incoming.transferredPercentage ?? null,
  };
  if (!registryHasMeaningfulData(section)) return null;
  if (provenance) section.provenance = provenance;
  return section;
}

/**
 * Compare assessment reference recency. Prefer ISO referenceDate, else referenceYear.
 * Returns: newer | older | equal | incomparable
 */
export function compareAssessmentRecency(existingAssessment, incomingAssessment) {
  const existing = existingAssessment && typeof existingAssessment === "object" ? existingAssessment : {};
  const incoming = incomingAssessment && typeof incomingAssessment === "object" ? incomingAssessment : {};
  const existingDate = typeof existing.referenceDate === "string" ? existing.referenceDate : null;
  const incomingDate = typeof incoming.referenceDate === "string" ? incoming.referenceDate : null;
  if (existingDate && incomingDate) {
    if (incomingDate > existingDate) return "newer";
    if (incomingDate < existingDate) return "older";
    return "equal";
  }
  if (existingDate && !incomingDate) return "older"; // undated must not replace dated
  if (!existingDate && incomingDate) return "newer";
  const existingYear = Number.isInteger(existing.referenceYear) ? existing.referenceYear : null;
  const incomingYear = Number.isInteger(incoming.referenceYear) ? incoming.referenceYear : null;
  if (existingYear != null && incomingYear != null) {
    if (incomingYear > existingYear) return "newer";
    if (incomingYear < existingYear) return "older";
    return "equal";
  }
  if (existingYear != null && incomingYear == null) return "older";
  if (existingYear == null && incomingYear != null) return "newer";
  return "incomparable";
}

/**
 * Compare registry activity dates (normalized ISO YYYY-MM-DD).
 * Returns: newer | older | equal | incomparable
 */
export function compareRegistryRecency(existingActivity, incomingActivity) {
  const existingDate =
    existingActivity && typeof existingActivity.activityDate === "string"
      ? existingActivity.activityDate
      : null;
  const incomingDate =
    incomingActivity && typeof incomingActivity.activityDate === "string"
      ? incomingActivity.activityDate
      : null;
  if (existingDate && incomingDate) {
    if (incomingDate > existingDate) return "newer";
    if (incomingDate < existingDate) return "older";
    return "equal";
  }
  if (existingDate && !incomingDate) return "older"; // undated must not replace dated
  if (!existingDate && incomingDate) return "newer";
  return "incomparable";
}

function registryEventIdentity(activity) {
  if (!activity || typeof activity !== "object") return null;
  const registration =
    typeof activity.registrationNumber === "string" && activity.registrationNumber.trim()
      ? activity.registrationNumber.trim()
      : null;
  if (registration) return `reg:${registration}`;
  if (activity.activityType) return `type:${activity.activityType}`;
  return null;
}

function assessmentsCompatibleForEnrich(existing, incoming) {
  for (const key of ["totalCents", "landCents", "buildingCents", "referenceYear", "referenceDate"]) {
    const a = existing[key];
    const b = incoming[key];
    if (a == null || a === "" || b == null || b === "") continue;
    if (!valuesEqual(a, b)) return false;
  }
  return true;
}

function registryCompatibleForEnrich(existing, incoming) {
  for (const key of [
    "activityType",
    "originalLabel",
    "activityDate",
    "considerationCents",
    "registrationNumber",
    "transferredPercentage",
  ]) {
    const a = existing[key];
    const b = incoming[key];
    if (a == null || a === "" || b == null || b === "") continue;
    if (!valuesEqual(a, b)) return false;
  }
  return true;
}

/**
 * Treat municipal assessment as a coherent snapshot keyed by reference year/date.
 * Never silently mix values from different rolls into one assessment.
 */
export function mergeImportedAssessmentSection(existingAssessment, incomingAssessment, provenance) {
  if (!assessmentHasMeaningfulData(incomingAssessment)) {
    return {
      section: assessmentHasMeaningfulData(existingAssessment) ? existingAssessment || null : null,
      changed: false,
      outcome: "preserved_blank",
    };
  }
  if (!assessmentHasMeaningfulData(existingAssessment)) {
    return {
      section: buildAssessmentSnapshot(incomingAssessment, provenance),
      changed: true,
      outcome: "replaced",
    };
  }

  const existing =
    existingAssessment && typeof existingAssessment === "object" ? existingAssessment : {};
  const incoming =
    incomingAssessment && typeof incomingAssessment === "object" ? incomingAssessment : {};
  const recency = compareAssessmentRecency(existing, incoming);

  if (recency === "older") {
    return { section: existingAssessment, changed: false, outcome: "ignored_older" };
  }
  if (recency === "newer") {
    return {
      section: buildAssessmentSnapshot(incoming, provenance),
      changed: true,
      outcome: "replaced",
    };
  }
  if (recency === "equal") {
    if (!assessmentsCompatibleForEnrich(existing, incoming)) {
      return { section: existingAssessment, changed: false, outcome: "rejected_conflict" };
    }
    const merged = { ...existing };
    let changed = false;
    for (const key of ["totalCents", "landCents", "buildingCents", "referenceYear", "referenceDate"]) {
      const value = incoming[key];
      if (value == null || value === "") continue;
      if (valuesEqual(existing[key], value)) continue;
      merged[key] = value;
      changed = true;
    }
    if (!changed) {
      return { section: existingAssessment, changed: false, outcome: "identical" };
    }
    if (provenance) merged.provenance = provenance;
    return { section: merged, changed: true, outcome: "enriched" };
  }
  // incomparable (both undated): overlay nonblank fields onto the same undated snapshot.
  {
    const merged = { ...existing };
    let changed = false;
    for (const key of ["totalCents", "landCents", "buildingCents", "referenceYear", "referenceDate"]) {
      const value = incoming[key];
      if (value == null || value === "") continue;
      if (valuesEqual(existing[key], value)) continue;
      merged[key] = value;
      changed = true;
    }
    if (!changed) {
      return { section: existingAssessment, changed: false, outcome: "identical" };
    }
    if (provenance) merged.provenance = provenance;
    return { section: merged, changed: true, outcome: "enriched" };
  }
}

/**
 * Treat latestRegistryActivity as one coherent event snapshot.
 * Blank incoming preserves existing. Newer events replace the entire prior event
 * (missing consideration stays null — never inherited from another event).
 */
export function mergeImportedRegistrySection(existingActivity, incomingActivity, provenance) {
  if (!registryHasMeaningfulData(incomingActivity)) {
    return {
      section: registryHasMeaningfulData(existingActivity) ? existingActivity || null : null,
      changed: false,
      outcome: "preserved_blank",
    };
  }
  if (!registryHasMeaningfulData(existingActivity)) {
    return {
      section: buildRegistrySnapshot(incomingActivity, provenance),
      changed: true,
      outcome: "replaced",
    };
  }

  const existing = existingActivity && typeof existingActivity === "object" ? existingActivity : {};
  const incoming = incomingActivity && typeof incomingActivity === "object" ? incomingActivity : {};
  const recency = compareRegistryRecency(existing, incoming);

  if (recency === "older") {
    return { section: existingActivity, changed: false, outcome: "ignored_older" };
  }
  if (recency === "newer") {
    return {
      section: buildRegistrySnapshot(incoming, provenance),
      changed: true,
      outcome: "replaced",
    };
  }
  if (recency === "equal") {
    const existingId = registryEventIdentity(existing);
    const incomingId = registryEventIdentity(incoming);
    const typesConflict =
      existing.activityType &&
      incoming.activityType &&
      existing.activityType !== incoming.activityType;
    const idsConflict =
      existingId &&
      incomingId &&
      existingId.startsWith("reg:") &&
      incomingId.startsWith("reg:") &&
      existingId !== incomingId;
    if (typesConflict || idsConflict || !registryCompatibleForEnrich(existing, incoming)) {
      return { section: existingActivity, changed: false, outcome: "rejected_conflict" };
    }
    const merged = { ...existing };
    let changed = false;
    for (const key of [
      "activityType",
      "originalLabel",
      "activityDate",
      "considerationCents",
      "registrationNumber",
      "transferredPercentage",
    ]) {
      const value = incoming[key];
      if (value == null || value === "") continue;
      if (valuesEqual(existing[key], value)) continue;
      merged[key] = value;
      changed = true;
    }
    if (!changed) {
      return { section: existingActivity, changed: false, outcome: "identical" };
    }
    if (provenance) merged.provenance = provenance;
    return { section: merged, changed: true, outcome: "enriched" };
  }
  // incomparable (both undated): enrich only when compatible; else reject
  if (!registryCompatibleForEnrich(existing, incoming)) {
    return { section: existingActivity, changed: false, outcome: "rejected_conflict" };
  }
  const merged = { ...existing };
  let changed = false;
  for (const key of [
    "activityType",
    "originalLabel",
    "activityDate",
    "considerationCents",
    "registrationNumber",
    "transferredPercentage",
  ]) {
    const value = incoming[key];
    if (value == null || value === "") continue;
    if (valuesEqual(existing[key], value)) continue;
    merged[key] = value;
    changed = true;
  }
  if (!changed) return { section: existingActivity, changed: false, outcome: "identical" };
  if (provenance) merged.provenance = provenance;
  return { section: merged, changed: true, outcome: "enriched" };
}

/**
 * Exact Firestore version token (seconds + nanoseconds). Never millisecond-only.
 * Fail-closed: returns null when the value cannot be decoded exactly enough for CAS,
 * except Date/number/ISO inputs which are encoded at millisecond resolution as n = (ms%1000)*1e6.
 */
export function encodeFirestoreVersionToken(value) {
  if (value == null) return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    if (Number.isInteger(value.s) && Number.isInteger(value.n) && value.n >= 0 && value.n < 1e9) {
      return { s: value.s, n: value.n };
    }
    const seconds = value.seconds ?? value._seconds;
    const nanos = value.nanoseconds ?? value._nanoseconds;
    if (typeof seconds === "number" && Number.isFinite(seconds)) {
      const s = Number(seconds);
      const n = typeof nanos === "number" && Number.isFinite(nanos) ? Number(nanos) : 0;
      if (!Number.isInteger(s) || !Number.isInteger(n) || n < 0 || n >= 1e9) return null;
      return { s, n };
    }
    if (typeof value.toMillis === "function" && typeof value.seconds === "number") {
      const s = Number(value.seconds);
      const n = Number(value.nanoseconds || 0);
      if (!Number.isInteger(s) || !Number.isInteger(n) || n < 0 || n >= 1e9) return null;
      return { s, n };
    }
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    if (!Number.isFinite(ms)) return null;
    const s = Math.floor(ms / 1000);
    const n = (ms % 1000) * 1e6;
    return { s, n };
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const s = Math.floor(value / 1000);
    const n = (value % 1000) * 1e6;
    return { s, n };
  }
  if (typeof value === "string") {
    // Accept explicit tokens "s:n" or ISO (ms precision only).
    const token = value.trim();
    const m = token.match(/^(\d+):(\d+)$/);
    if (m) {
      const s = Number(m[1]);
      const n = Number(m[2]);
      if (!Number.isInteger(s) || !Number.isInteger(n) || n < 0 || n >= 1e9) return null;
      return { s, n };
    }
    const ms = Date.parse(token);
    if (!Number.isFinite(ms)) return null;
    return { s: Math.floor(ms / 1000), n: (ms % 1000) * 1e6 };
  }
  return null;
}

export function firestoreVersionsEqual(a, b) {
  const left = encodeFirestoreVersionToken(a);
  const right = encodeFirestoreVersionToken(b);
  if (!left || !right) return false;
  return left.s === right.s && left.n === right.n;
}

export function serializeFirestoreVersionToken(value) {
  const token = encodeFirestoreVersionToken(value);
  if (!token) return null;
  return `${token.s}:${token.n}`;
}

/**
 * Immutable import-row ownership marker (no raw CSV / PII / money).
 */
export function buildImportRowMarker(importId, rowNumber, kind, targetId = null) {
  const id = typeof importId === "string" ? importId.trim() : "";
  const n = Number.isInteger(rowNumber) ? rowNumber : 0;
  const op = kind === "update" ? "update" : "create";
  const target = typeof targetId === "string" && targetId.trim() ? targetId.trim() : "";
  return `irm:v1:${id}:r${n}:${op}${target ? `:${target}` : ""}`.slice(0, 240);
}

const ADMIN_PROPERTY_DATA_PATCH_TOP_KEYS = new Set([
  "lotNumbers",
  "cadastralDivision",
  "municipalRollNumber",
  "numberOfFloors",
  "constructionYear",
  "landArea",
  "municipalAssessment",
  "registryActivity",
  "numberOfUnits",
]);

const MUNICIPAL_ASSESSMENT_PATCH_KEYS = new Set([
  "totalCents",
  "landCents",
  "buildingCents",
  "referenceYear",
  "referenceDate",
]);

const REGISTRY_ACTIVITY_PATCH_KEYS = new Set([
  "activityType",
  "originalLabel",
  "activityDate",
  "considerationCents",
  "registrationNumber",
  "transferredPercentage",
]);

function parseOptionalCents(raw, { allowZero = true } = {}) {
  if (raw === null) return { ok: true, value: null };
  if (!Number.isInteger(raw) || raw < 0) return { ok: false, reason: "invalid" };
  if (raw === 0 && !allowZero) return { ok: true, value: null };
  if (raw > JLR_MONEY_MAX_CENTS) return { ok: false, reason: "upperBound" };
  return { ok: true, value: raw };
}

function parseOptionalAssessmentYear(raw) {
  if (raw === null) return { ok: true, value: null };
  if (!Number.isInteger(raw) || raw < 1800 || raw > 2100) return { ok: false, reason: "invalid" };
  return { ok: true, value: raw };
}

function parseOptionalAssessmentDate(raw) {
  if (raw === null) return { ok: true, value: null };
  const parsed = parseJlrActivityDate(raw);
  if (!parsed.ok) return { ok: false, reason: parsed.reason };
  return { ok: true, value: parsed.isoDate };
}

function parseOptionalRegistryActivityType(raw) {
  if (raw === null) return { ok: true, value: null, originalLabel: null };
  if (!REGISTRY_ACTIVITY_TYPES.includes(raw)) return { ok: false, reason: "invalid" };
  return { ok: true, value: raw, originalLabel: null };
}

function parseOptionalRegistryLabel(raw) {
  if (raw === null) return { ok: true, value: null };
  const result = boundedTrim(raw, REGISTRY_LABEL_MAX_LEN);
  if (result && typeof result === "object" && result.error) return { ok: false, reason: result.error };
  return { ok: true, value: result };
}

function parseOptionalRegistrationNumber(raw) {
  if (raw === null) return { ok: true, value: null };
  const result = boundedTrim(raw, REGISTRATION_NUMBER_MAX_LEN);
  if (result && typeof result === "object" && result.error) return { ok: false, reason: result.error };
  return { ok: true, value: result };
}

function parseOptionalTransferredPercentage(raw) {
  if (raw === null) return { ok: true, value: null };
  const n = parseFiniteNumber(raw);
  if (n == null || n < 0 || n > 100) return { ok: false, reason: "invalid" };
  return { ok: true, value: Math.round(n * 10000) / 10000 };
}

function parseOptionalLandAreaPatch(raw) {
  if (raw === null) return { ok: true, landArea: null };
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, reason: "invalid" };
  for (const key of Object.keys(raw)) {
    if (key !== "value" && key !== "unit") return { ok: false, reason: "invalid" };
  }
  return parseLandArea(raw.value, raw.unit);
}

/**
 * Parse admin manual property-data patch. Omitted keys are not returned.
 * Explicit null clears optional scalar fields; nested objects merge per-field.
 */
export function parseAdminPropertyDataPatch(patch, { nowYear = new Date().getFullYear() } = {}) {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    return { ok: false, reason: "invalid" };
  }
  for (const key of Object.keys(patch)) {
    if (!ADMIN_PROPERTY_DATA_PATCH_TOP_KEYS.has(key)) return { ok: false, reason: "unknown" };
  }
  const parsed = {};

  if (Object.prototype.hasOwnProperty.call(patch, "lotNumbers")) {
    if (patch.lotNumbers === null) {
      parsed.lotNumbers = [];
    } else if (!Array.isArray(patch.lotNumbers)) {
      return { ok: false, reason: "lotNumbers" };
    } else {
      const lots = normalizeLotNumbers(patch.lotNumbers);
      if (!lots.ok) return { ok: false, reason: "lotNumbers" };
      parsed.lotNumbers = lots.lotNumbers;
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "cadastralDivision")) {
    if (patch.cadastralDivision === null) parsed.cadastralDivision = null;
    else {
      const division = parseCadastralDivision(patch.cadastralDivision);
      if (!division.ok) return { ok: false, reason: "cadastralDivision" };
      parsed.cadastralDivision = division.value;
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "municipalRollNumber")) {
    if (patch.municipalRollNumber === null) parsed.municipalRollNumber = null;
    else {
      const roll = parseMunicipalRollNumber(patch.municipalRollNumber);
      if (!roll.ok) return { ok: false, reason: "municipalRollNumber" };
      parsed.municipalRollNumber = roll.value;
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "numberOfFloors")) {
    if (patch.numberOfFloors === null) parsed.numberOfFloors = null;
    else {
      const floors = parseNumberOfFloors(patch.numberOfFloors);
      if (!floors.ok) return { ok: false, reason: "numberOfFloors" };
      parsed.numberOfFloors = floors.floors;
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "constructionYear")) {
    if (patch.constructionYear === null) parsed.constructionYear = null;
    else {
      const year = parseConstructionYear(patch.constructionYear, { nowYear });
      if (!year.ok) return { ok: false, reason: "constructionYear" };
      parsed.constructionYear = year.year;
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "landArea")) {
    const landArea = parseOptionalLandAreaPatch(patch.landArea);
    if (!landArea.ok) return { ok: false, reason: "landArea" };
    parsed.landArea = landArea.landArea;
  }

  if (Object.prototype.hasOwnProperty.call(patch, "numberOfUnits")) {
    if (patch.numberOfUnits === null) parsed.numberOfUnits = null;
    else if (!Number.isInteger(patch.numberOfUnits) || patch.numberOfUnits <= 0) {
      return { ok: false, reason: "numberOfUnits" };
    } else {
      parsed.numberOfUnits = patch.numberOfUnits;
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "municipalAssessment")) {
    const src = patch.municipalAssessment;
    if (src === null) {
      parsed.municipalAssessment = null;
    } else if (!src || typeof src !== "object" || Array.isArray(src)) {
      return { ok: false, reason: "municipalAssessment" };
    } else {
      for (const key of Object.keys(src)) {
        if (!MUNICIPAL_ASSESSMENT_PATCH_KEYS.has(key)) return { ok: false, reason: "municipalAssessment" };
      }
      const assessment = {};
      if (Object.prototype.hasOwnProperty.call(src, "totalCents")) {
        const cents = parseOptionalCents(src.totalCents);
        if (!cents.ok) return { ok: false, reason: "municipalAssessment.totalCents" };
        assessment.totalCents = cents.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "landCents")) {
        const cents = parseOptionalCents(src.landCents);
        if (!cents.ok) return { ok: false, reason: "municipalAssessment.landCents" };
        assessment.landCents = cents.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "buildingCents")) {
        const cents = parseOptionalCents(src.buildingCents);
        if (!cents.ok) return { ok: false, reason: "municipalAssessment.buildingCents" };
        assessment.buildingCents = cents.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "referenceYear")) {
        const year = parseOptionalAssessmentYear(src.referenceYear);
        if (!year.ok) return { ok: false, reason: "municipalAssessment.referenceYear" };
        assessment.referenceYear = year.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "referenceDate")) {
        const date = parseOptionalAssessmentDate(src.referenceDate);
        if (!date.ok) return { ok: false, reason: "municipalAssessment.referenceDate" };
        assessment.referenceDate = date.value;
      }
      parsed.municipalAssessment = assessment;
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, "registryActivity")) {
    const src = patch.registryActivity;
    if (src === null) {
      parsed.registryActivity = null;
    } else if (!src || typeof src !== "object" || Array.isArray(src)) {
      return { ok: false, reason: "registryActivity" };
    } else {
      for (const key of Object.keys(src)) {
        if (!REGISTRY_ACTIVITY_PATCH_KEYS.has(key)) return { ok: false, reason: "registryActivity" };
      }
      const activity = {};
      if (Object.prototype.hasOwnProperty.call(src, "activityType")) {
        const type = parseOptionalRegistryActivityType(src.activityType);
        if (!type.ok) return { ok: false, reason: "registryActivity.activityType" };
        activity.activityType = type.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "originalLabel")) {
        const label = parseOptionalRegistryLabel(src.originalLabel);
        if (!label.ok) return { ok: false, reason: "registryActivity.originalLabel" };
        activity.originalLabel = label.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "activityDate")) {
        const date = parseOptionalAssessmentDate(src.activityDate);
        if (!date.ok) return { ok: false, reason: "registryActivity.activityDate" };
        activity.activityDate = date.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "considerationCents")) {
        const cents = parseOptionalCents(src.considerationCents);
        if (!cents.ok) return { ok: false, reason: "registryActivity.considerationCents" };
        activity.considerationCents = cents.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "registrationNumber")) {
        const number = parseOptionalRegistrationNumber(src.registrationNumber);
        if (!number.ok) return { ok: false, reason: "registryActivity.registrationNumber" };
        activity.registrationNumber = number.value;
      }
      if (Object.prototype.hasOwnProperty.call(src, "transferredPercentage")) {
        const pct = parseOptionalTransferredPercentage(src.transferredPercentage);
        if (!pct.ok) return { ok: false, reason: "registryActivity.transferredPercentage" };
        activity.transferredPercentage = pct.value;
      }
      parsed.registryActivity = activity;
    }
  }

  return { ok: true, patch: parsed };
}

function profileHasData(profile) {
  if (!profile || typeof profile !== "object") return false;
  return Boolean(
    profile.buildingCategory ||
      profile.constructionType ||
      profile.numberOfUnits != null ||
      profile.numberOfFloors != null ||
      profile.constructionYear != null ||
      profile.landArea ||
      (Array.isArray(profile.lotNumbers) && profile.lotNumbers.length) ||
      profile.cadastralDivision ||
      profile.municipalRollNumber,
  );
}

function assessmentHasData(assessment) {
  if (!assessment || typeof assessment !== "object") return false;
  return (
    assessment.totalCents != null ||
    assessment.landCents != null ||
    assessment.buildingCents != null ||
    assessment.referenceYear != null ||
    assessment.referenceDate != null
  );
}

function activityHasData(activity) {
  if (!activity || typeof activity !== "object") return false;
  return Boolean(
    activity.activityType ||
      activity.originalLabel ||
      activity.activityDate ||
      activity.considerationCents != null ||
      activity.registrationNumber ||
      activity.transferredPercentage != null,
  );
}

/**
 * Merge an admin property-data patch into stored prospect fields.
 * Omitted patch keys preserve existing nested values; explicit null clears.
 * Only sections/fields that actually change are included in the write patch,
 * and provenance is updated only for those changed sections.
 */
export function buildAdminPropertyDataWritePatch(existing, patch, provenance) {
  const current = existing && typeof existing === "object" ? existing : {};
  const parsedPatch = patch && typeof patch === "object" ? patch : {};
  const changedFields = [];
  const writePatch = {};

  const existingProfile =
    current.latestImportedPropertyProfile && typeof current.latestImportedPropertyProfile === "object"
      ? { ...current.latestImportedPropertyProfile }
      : {};

  let lotNumbers = Array.isArray(current.lotNumbers)
    ? [...current.lotNumbers]
    : Array.isArray(existingProfile.lotNumbers)
      ? [...existingProfile.lotNumbers]
      : [];
  let cadastralDivision = current.cadastralDivision ?? existingProfile.cadastralDivision ?? null;
  let municipalRollNumber = current.municipalRollNumber ?? existingProfile.municipalRollNumber ?? null;

  let profileTouched = false;
  const nextProfile = { ...existingProfile };

  if (Object.prototype.hasOwnProperty.call(parsedPatch, "lotNumbers")) {
    if (!valuesEqual(lotNumbers, parsedPatch.lotNumbers)) {
      lotNumbers = parsedPatch.lotNumbers;
      writePatch.lotNumbers = lotNumbers;
      changedFields.push("lotNumbers");
      profileTouched = true;
    }
  }
  if (Object.prototype.hasOwnProperty.call(parsedPatch, "cadastralDivision")) {
    if (!valuesEqual(cadastralDivision, parsedPatch.cadastralDivision)) {
      cadastralDivision = parsedPatch.cadastralDivision;
      writePatch.cadastralDivision = cadastralDivision;
      changedFields.push("cadastralDivision");
      profileTouched = true;
    }
  }
  if (Object.prototype.hasOwnProperty.call(parsedPatch, "municipalRollNumber")) {
    if (!valuesEqual(municipalRollNumber, parsedPatch.municipalRollNumber)) {
      municipalRollNumber = parsedPatch.municipalRollNumber;
      writePatch.municipalRollNumber = municipalRollNumber;
      changedFields.push("municipalRollNumber");
      profileTouched = true;
    }
  }

  if (Object.prototype.hasOwnProperty.call(parsedPatch, "numberOfFloors")) {
    if (!valuesEqual(existingProfile.numberOfFloors ?? null, parsedPatch.numberOfFloors)) {
      nextProfile.numberOfFloors = parsedPatch.numberOfFloors;
      changedFields.push("numberOfFloors");
      profileTouched = true;
    }
  }
  if (Object.prototype.hasOwnProperty.call(parsedPatch, "constructionYear")) {
    if (!valuesEqual(existingProfile.constructionYear ?? null, parsedPatch.constructionYear)) {
      nextProfile.constructionYear = parsedPatch.constructionYear;
      changedFields.push("constructionYear");
      profileTouched = true;
    }
  }
  if (Object.prototype.hasOwnProperty.call(parsedPatch, "landArea")) {
    if (!valuesEqual(existingProfile.landArea ?? null, parsedPatch.landArea)) {
      nextProfile.landArea = parsedPatch.landArea;
      changedFields.push("landArea");
      profileTouched = true;
    }
  }

  if (profileTouched) {
    nextProfile.lotNumbers = lotNumbers;
    nextProfile.cadastralDivision = cadastralDivision;
    nextProfile.municipalRollNumber = municipalRollNumber;
    nextProfile.provenance = provenance;
    writePatch.latestImportedPropertyProfile = profileHasData(nextProfile) ? nextProfile : null;
    writePatch.lotMatchKeys = buildLotMatchKeys(lotNumbers, cadastralDivision);
    if (!changedFields.includes("latestImportedPropertyProfile")) {
      changedFields.push("latestImportedPropertyProfile");
    }
  }

  if (Object.prototype.hasOwnProperty.call(parsedPatch, "municipalAssessment")) {
    const existingAssessment =
      current.latestMunicipalAssessment && typeof current.latestMunicipalAssessment === "object"
        ? { ...current.latestMunicipalAssessment }
        : {};
    let nextAssessment = null;
    if (parsedPatch.municipalAssessment === null) {
      nextAssessment = null;
    } else {
      nextAssessment = { ...existingAssessment };
      for (const [key, value] of Object.entries(parsedPatch.municipalAssessment)) {
        nextAssessment[key] = value;
      }
      nextAssessment = assessmentHasData(nextAssessment) ? nextAssessment : null;
    }
    const existingForCompare = assessmentHasData(existingAssessment)
      ? stripProv(existingAssessment)
      : null;
    const nextForCompare = nextAssessment ? stripProv(nextAssessment) : null;
    if (!valuesEqual(existingForCompare, nextForCompare)) {
      if (nextAssessment) nextAssessment.provenance = provenance;
      writePatch.latestMunicipalAssessment = nextAssessment;
      changedFields.push("latestMunicipalAssessment");
    }
  }

  if (Object.prototype.hasOwnProperty.call(parsedPatch, "registryActivity")) {
    const existingActivity =
      current.latestRegistryActivity && typeof current.latestRegistryActivity === "object"
        ? { ...current.latestRegistryActivity }
        : null;
    let nextActivity = null;
    if (parsedPatch.registryActivity === null) {
      nextActivity = null;
    } else {
      nextActivity = { ...(existingActivity || {}) };
      for (const [key, value] of Object.entries(parsedPatch.registryActivity)) {
        nextActivity[key] = value;
      }
      nextActivity = activityHasData(nextActivity) ? nextActivity : null;
    }
    const existingForCompare = existingActivity ? stripProv(existingActivity) : null;
    const nextForCompare = nextActivity ? stripProv(nextActivity) : null;
    if (!valuesEqual(existingForCompare, nextForCompare)) {
      if (nextActivity) nextActivity.provenance = provenance;
      writePatch.latestRegistryActivity = nextActivity;
      changedFields.push("latestRegistryActivity");
    }
  }

  if (Object.prototype.hasOwnProperty.call(parsedPatch, "numberOfUnits")) {
    if (!valuesEqual(current.numberOfUnits ?? null, parsedPatch.numberOfUnits)) {
      writePatch.numberOfUnits = parsedPatch.numberOfUnits;
      changedFields.push("numberOfUnits");
    }
  }

  return {
    writePatch,
    changedFields: [...new Set(changedFields)].sort(),
    noop: changedFields.length === 0,
  };
}

function stripProv(value) {
  if (!value || typeof value !== "object") return value ?? null;
  const { provenance: _p, ...rest } = value;
  return rest;
}
